import React, { useRef } from 'react'
import styled from 'styled-components'
import { Publish, Close } from '@material-ui/icons'

import { Spacings, Text } from '@styles'

const DropZone = styled.button`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
	border: 1px dashed ${({ theme, disabled }) => (disabled ? theme.colors.border.default : theme.colors.brand.primary)};
	border-radius: ${({ theme }) => theme.radius.md};
	background: ${({ theme, disabled }) => (disabled ? theme.colors.surface.sunken : theme.colors.brand.subtle)};
	color: ${({ theme }) => theme.colors.text.secondary};
	cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
	font-family: ${({ theme }) => theme.fontFamily.inter};
	transition: opacity 0.15s ease-in-out;

	&:hover:not(:disabled) {
		opacity: 0.85;
	}

	&:disabled {
		opacity: 0.6;
	}
`

const UploadIcon = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: ${Spacings.SPACING_10};
	height: ${Spacings.SPACING_10};
	border-radius: ${({ theme }) => theme.radius.full};
	background: ${({ theme }) => theme.colors.surface.card};
	color: ${({ theme, $disabled }) => ($disabled ? theme.colors.text.secondary : theme.colors.brand.primary)};

	& > svg {
		font-size: ${Text.X_LARGE};
	}
`

const DropText = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_500};
	color: ${({ theme }) => theme.colors.text.primary};
`

const FileList = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_2B};
	margin-top: ${Spacings.SPACING_2B};
`

const FileChip = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
	padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};
	border: 1px solid ${({ theme }) => theme.colors.brand.primary};
	border-radius: ${({ theme }) => theme.radius.md};
	background: ${({ theme }) => theme.colors.surface.card};
`

const FileName = styled.span`
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.brand.primary};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

const RemoveBtn = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border: none;
	background: transparent;
	cursor: pointer;
	color: ${({ theme }) => theme.colors.brand.primary};
	flex-shrink: 0;

	& > svg {
		font-size: ${Text.LARGE};
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.4;
	}
`

const HiddenInput = styled.input`
	display: none;
`

export const DEFAULT_UPLOAD_ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.png,.txt,.csv,.ppt,.pptx'

// v2 upload dropzone. Click opens the file picker; picked files are listed below as removable
// chips. `disabled` makes the whole control read-only — the picker never opens and files can't
// be removed, so it also works as a plain "these are the attached files" view.
const UploadButton = ({
	files = [],
	onChange,
	label = 'Tap to Upload Documents',
	accept = DEFAULT_UPLOAD_ACCEPT,
	multiple = true,
	disabled = false,
	className,
}) => {
	const inputRef = useRef()

	const openPicker = () => {
		if (disabled) return
		inputRef.current?.click()
	}

	const handleFiles = (fileList) => {
		const picked = Array.from(fileList || [])
		if (picked.length) onChange?.(multiple ? [...files, ...picked] : picked.slice(0, 1))
	}

	const removeFile = (index) => onChange?.(files.filter((_, i) => i !== index))

	return (
		<div className={className}>
			<DropZone type='button' onClick={openPicker} disabled={disabled}>
				<UploadIcon $disabled={disabled}>
					<Publish />
				</UploadIcon>
				<DropText>{label}</DropText>
			</DropZone>
			<HiddenInput
				ref={inputRef}
				type='file'
				multiple={multiple}
				accept={accept}
				disabled={disabled}
				onChange={(e) => {
					handleFiles(e.target.files)
					e.target.value = ''
				}}
			/>
			{files.length > 0 && (
				<FileList>
					{files.map((file, index) => (
						<FileChip key={`${file.name}-${index}`}>
							<FileName>{file.name}</FileName>
							<RemoveBtn type='button' onClick={() => removeFile(index)} title='Remove' disabled={disabled}>
								<Close />
							</RemoveBtn>
						</FileChip>
					))}
				</FileList>
			)}
		</div>
	)
}

export default UploadButton
